import { MRT_ColumnDef } from 'mantine-react-table';
import { Text } from '@mantine/core';
import type { CSSProperties } from 'react';
import { formatNumberToCurrency, formatPriceAsEntered } from 'app/utils/util-functions';
import { RefinedSubTrade, TradedFacilityParams } from 'app/models/traded-facilities-params';
import { useNavigate } from 'react-router'
import SellerPositionHoverCard from 'app/views/components/seller-position-hover-card/seller-position-hover-card';
import { facilityStatusInfo } from '../../trade-enty-page-columnDefs';
// import CustomHoverCard from 'app/views/components/popover/custom-hover-card';


const getTextStyle = (color: string, weight = 600, textAlign?: 'start' | 'end' | 'center'): CSSProperties => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: weight,
    lineHeight: '19px',
    textAlign: textAlign ?? 'center',
    color,
    width: '100%',
});

function TableHead({ header, textAlign }: { header: string, textAlign?: 'start' | 'end' | 'center' }) {
    return (
        <Text style={getTextStyle('#784595', 400, textAlign)}>
            {header}
        </Text>
    );
}

// function to create cells with seller position hover cards
function createEntityCell(columnType: 'myEntity' | 'counterparty') {
    return ({ row }: { row: any }) => {
        // only show hover card for selling entity - will be different column depending on whether buy or sell
        const entity = columnType === 'myEntity'
            ? (row.original.myEntity === 'Buy' ? row.original.buyerEntity : row.original.sellerEntity)
            : (row.original.myEntity === 'Sell' ? row.original.buyerEntity : row.original.sellerEntity);

        const entityName = entity?.entityName ?? 'Unknown';
        const entityMei = entity?.mei ?? 'N/A';

        const isShowingSeller = columnType === 'myEntity'
            ? row.original.myEntity === 'Sell'
            : row.original.myEntity === 'Buy';

        const sellerEntity = row.original.sellerEntity;

        const facilityIds = row.original.tradedFacilities?.map((tf: any) => tf.facilityId?.id).filter((id: any): id is string => !!id) || [];

        // only show seller position hover card if trades are draft or open status
        const subtradeStatus = (row.original.status ?? '').toLowerCase();
        const shouldShowHoverCard = isShowingSeller && (subtradeStatus === 'open' || subtradeStatus === 'draft');

        const nameElement = (
            <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                {entityName}
            </Text>
        );

        return (
            <div>
                {shouldShowHoverCard ? (
                    <SellerPositionHoverCard
                        sellerId={sellerEntity?.id || ''}
                        facilityIds={facilityIds}
                    >
                        {nameElement}
                    </SellerPositionHoverCard>
                ) : (
                    nameElement
                )}
                <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'start' }}>{entityMei}</Text>
            </div>
        );
    };
}

export const columnDefs: MRT_ColumnDef<RefinedSubTrade>[] = [
    {
        size: 50,
        accessorKey: 'status',
        header: 'Status',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: <div style={{ width: "100%" }}><TableHead header={'Status'} /></div>,
        Cell: ({ cell }) => {
            const status = (cell.getValue() as string ?? '').toLowerCase();
            const statusColors: { [key: string]: string } = {
                open: '#287A26',
                settled: '#EE5959',
                draft: '#757373',
                canceled: '#EE5959',
                terminated: '#E8006E',
            };

            return (
                <Text style={getTextStyle(statusColors[status] || '#000000')}>
                    {cell.getValue() as string ?? ''}
                </Text>
            );
        },
    },
    {
        size: 80,
        header: 'Trade Ids',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'SubTrade ID'} textAlign='start' />
                <TableHead header={'Internal ID'} textAlign='start' />
            </div>
        ),
        Cell: ({ row }) => {
            const subTradeId = row.original.systemId ?? row.original.id;
            const clientId = row.original.clientId;
            return (
                <div>
                    <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                        {subTradeId}
                    </Text>
                    <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'start' }}>
                        {clientId}
                    </Text>
                </div>
            );
        },
    },

    {
        size: 60,
        header: 'Trade Action & Date',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Trade Action'} />
                <TableHead header={'Trade Date'} />
            </div>
        ), Cell: ({ row }) => {
            return (
                <div>
                    <Text style={getTextStyle('#413E3E')}>{row.original.masterTrade?.myEntity}</Text>
                    <Text style={getTextStyle('#413E3E')}>{row.original.masterTrade?.tradeDate}</Text>
                </div>
            );
        },
    },

    {
        size: 60,
        header: 'Target Settlement Date',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Target Settlement Date'} />
                <TableHead header={'T+'} />
            </div>
        ),
        Cell: ({ row }) => {
            const settlementDate = row.original.targetSettlementDate;
            const tPlus = row.original.tPlus;
            const isLoading = row.original.isLoadingAAMetadata;
            return (
                <div>
                    <Text style={getTextStyle('#476DB9')} data-test-id="settlement-date">{isLoading ? '...' : settlementDate}</Text>
                    <Text style={getTextStyle('#413E3E')}>{tPlus}</Text>
                </div>
            );
        },
    },

    {
        size: 60,
        header: 'My Entity',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'My Entity'} textAlign='start' />
                <TableHead header={'MEI'} textAlign='start' />
            </div>
        ),
        Cell: createEntityCell('myEntity'),
    },
    {
        size: 60,
        header: 'Counterparty',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Counterparty'} textAlign='start' />
                <TableHead header={'MEI'} textAlign='start' />
            </div>
        ),
        Cell: createEntityCell('counterparty'),
    },
    {
        size: 80,
        header: 'Deal & Facilities',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Deal (CUSIP)'} textAlign='start' />
                <TableHead header={'Facilities (CUSIP)'} textAlign='start' />
            </div>
        ),
        Cell: ({ row }) => {
            const navigate = useNavigate();

            const tradedFacilities = row.original.tradedFacilities || [];

            const refinedFacilities = tradedFacilities
                .map((facility: any) => facility.facilityDetails)
                .filter((facility: any) => facility !== null);

            const facilityTextObject = refinedFacilities.length > 1
                ? (<Text
                    className='multipleTradeFacilityText'
                >
                    Multiple Facilities {' '}
                    <span
                        className='multipleTradeFacilityTextCount'
                    >
                        ({refinedFacilities.length})
                    </span>
                </Text>)
                : (
                    <Text
                        className='singleTradeFacilityText'
                    >
                        {refinedFacilities[0]?.name + ' (' + (refinedFacilities[0]?.cusip || 'N/A') + ')'}
                    </Text>
                )

            return (
                <div>
                    <Text
                        style={{
                            cursor: 'pointer',
                            textAlign: 'start',
                        }}
                        onClick={() => {
                            if (!row.original.masterTrade?.dealId?.id)
                                return
                            navigate(`/dealmanagement/deal/${row.original.masterTrade?.dealId?.id}/Approved`)
                        }}
                        className='referenceDataText'
                    >
                        {row.original.dealName + ' ' + row.original.dealCusip}
                    </Text>
                    {facilityTextObject}
                </div>
            );
        },
    },
    {
        size: 60,
        header: 'Facility Status',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Facility Status'} textAlign='start' />
            </div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            const displays = Array.from(
                new Set(
                    tradedFacilities
                        .map((tf: any) => facilityStatusInfo[tf.facilityDetails?.status]?.display)
                        .filter(Boolean),
                ),
            ) as string[];
            if (displays.length === 0) return null;
            if (displays.length > 1) {
                return (
                    <Text className='multipleTradeFacilityText' style={{ textAlign: 'start' }}>
                        Multiple{' '}
                        <span className='multipleTradeFacilityTextCount'>
                            ({displays.length})
                        </span>
                    </Text>
                );
            }
            const [single] = displays;
            return (
                <Text
                    style={{
                        ...getTextStyle(facilityStatusInfo[single]?.color ?? '#413E3E'),
                        textAlign: 'start',
                    }}
                >
                    {single}
                </Text>
            );
        },
    },
    {
        size: 60,
        header: 'Price',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Price'} textAlign='end' />
            </div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities: TradedFacilityParams[] = row.original.tradedFacilities ?? [];
            // Price shows as entered at trade creation, with no padding to a fixed
            // precision — the ledger stores 98 as `98.0`, which reads as a price of
            // 98 rather than 98.0000000000 as Trade Assets renders it.
            // Deduping on the rendered value keeps 99.5 and 99.50 as a single price.
            const uniquePrices = Array.from(
                new Set(
                    tradedFacilities
                        .filter((tf) => tf.price?.trim() && Number.isFinite(Number(tf.price)))
                        .map((tf) => formatPriceAsEntered(tf.price)),
                ),
            );
            if (uniquePrices.length === 0) return null;
            if (uniquePrices.length > 1) {
                return (
                    <Text className='multipleTradeFacilityText' style={{ textAlign: 'end' }}>
                        Multiple{' '}
                        <span className='multipleTradeFacilityTextCount'>
                            ({uniquePrices.length})
                        </span>
                    </Text>
                );
            }
            const [price] = uniquePrices;
            return (
                <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'end' }}>
                    {price}
                </Text>
            );
        },
    },
    {
        size: 30,
        header: 'Traded Amount',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Traded Amount'} textAlign='end' />
            </div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            const totalTradeAmount = tradedFacilities.reduce((sum: number, facility: any) => sum + Number(facility.tradeAmount), 0);
            const currency = tradedFacilities.length > 0 ? tradedFacilities[0].facilityDetails?.baseCurrency ?? 'USD' : 'USD';
            return (
                <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'end' }}>
                    {formatNumberToCurrency(totalTradeAmount, currency)}
                </Text>
            );
        },
    },
    {
        size: 30,
        header: 'Current Amount',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Current Amount'} textAlign='end' />
            </div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            const totalCurrentAmount = tradedFacilities.reduce((sum: number, facility: any) => {
                const amounts = facility.amounts ?? [];
                return amounts.length > 0
                    ? row.original.lockedDate
                        ? sum + Number(facility.amounts?.findLast((amountWithDate: any) => new Date(amountWithDate.effectiveDate) <= new Date(row.original.lockedDate ?? ''))?.amount ?? "0.0")
                        : sum + Number(amounts[amounts.length - 1].amount)
                    : sum;
            }, 0);

            const currency = tradedFacilities.length > 0 ? tradedFacilities[0].facilityDetails?.baseCurrency ?? 'USD' : 'USD';
            return (
                <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'end' }}>
                    {formatNumberToCurrency(totalCurrentAmount, currency)}
                </Text>
            );
        },
    },
    // TEMPORARILY DISABLED per #6361 - Wire Instructions column removed due to performance issues
    // This column displays validation results computed by useWireInstructionValidation hook,
    // which triggers expensive async calculations for every row causing UI lag.
    // Will be replaced with PQS-based solution or redesigned approach.
    // {
    //     size: 30,
    //     header: 'Wire Instructions',
    //     mantineTableHeadCellProps: {
    //         className: 'cell-width-100',
    //     },
    //     Header: () => (
    //         <div style={{ width: '100%' }}>
    //             <TableHead header={'Wire Instructions'} />
    //         </div>
    //     ),
    //     Cell: ({ row }) => {
    //         // Use pre-calculated values from RefinedSubTrade
    //         const wireInstructionText = row.original.hasValidWireInstructions ?? '...';
    //         const missingParties = row.original.wireInstructionsMissingParties ?? [];
    //
    //         const color = wireInstructionText === '...' ? "#666666" :
    //             wireInstructionText === 'Yes' ? "#287A26" :
    //                 wireInstructionText === 'Error' ? "#FF6B35" : "#EE5959";
    //
    //         const textElement = (
    //             <Text style={getTextStyle(color)}>
    //                 {wireInstructionText}
    //             </Text>
    //         );
    //
    //         // Show hover card only when result is "No" and we have missing parties
    //         if (wireInstructionText === 'No' && missingParties.length > 0) {
    //             const hoverContent = (
    //                 <div style={{ zIndex: 1000 }}>
    //                     <Text className='hover-card-alert-text'>
    //                         Missing Wire Instructions: {missingParties.join(', ')}
    //                     </Text>
    //                 </div>
    //             );
    //
    //             return (
    //                 <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
    //                     <CustomHoverCard
    //                         targetComponent={textElement}
    //                         cardTitle="Wire Instructions"
    //                         width="280px"
    //                         position="left"
    //                     >
    //                         {hoverContent}
    //                     </CustomHoverCard>
    //                 </div>
    //             );
    //         }
    //
    //         return (
    //             <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
    //                 {textElement}
    //             </div>
    //         );
    //     },
    // },
];
